import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_SITE_NAME = 'Pussikuskit';
const DEFAULT_DESCRIPTION = 'Order food and groceries for delivery. Restaurants and stores delivered to your door.';
const DEFAULT_IMAGE = '/logo512.png';

/**
 * Get absolute URL for canonical/og:url (uses window in browser, fallback for SSR/build)
 */
const getBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://pussikuskit.fi';
};

/**
 * SEO component: sets document title, meta description, Open Graph and Twitter Card tags.
 * @param {string} title - Page title (appended with " | Pussikuskit" if not already containing site name)
 * @param {string} [description] - Meta description (defaults to DEFAULT_DESCRIPTION)
 * @param {string} [image] - Absolute or path image URL for og:image (defaults to DEFAULT_IMAGE)
 * @param {string} [path] - Path for canonical and og:url (e.g. "/restaurants"). If omitted, uses current path.
 * @param {boolean} [noIndex] - If true, adds robots noindex,nofollow
 */
const SEO = ({ title, description = DEFAULT_DESCRIPTION, image = DEFAULT_IMAGE, path, noIndex = false }) => {
  const baseUrl = getBaseUrl();
  const siteTitle = title && !title.includes(DEFAULT_SITE_NAME) ? `${title} | ${DEFAULT_SITE_NAME}` : (title || DEFAULT_SITE_NAME);
  const canonicalPath = path !== undefined ? path : (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/');
  const canonicalUrl = canonicalPath.startsWith('http') ? canonicalPath : `${baseUrl}${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`;
  const imageUrl = image.startsWith('http') ? image : `${baseUrl}${image.startsWith('/') ? '' : '/'}${image}`;

  return (
    <Helmet>
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={DEFAULT_SITE_NAME} />
      <meta property="og:locale" content="fi_FI" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
};

export default SEO;
